import type { ComponentDiagnostic } from "@swot/base-components";
import { Input, Button, RuntimeDiagnosticBanner } from "@swot/base-components";
import { SwotAppShell, SwotHeader, SwotMainContent, SwotSidebar, SwotBreadcrumb } from "@swot";
import { useCmtsDispatch, useCmtsSelector } from "@store/hooks.js";
import { selectCmtsAuth, selectCmtsMenuItems, selectCmtsPermissions } from "@store/selectors.js";
import type { ReactNode } from "react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import type { PermissionContext } from "@swot/swot-components";
import {
  buildCmtsMenuGroupsFromBackendMenu,
  cmtsBreadcrumbsForPath,
  filterCmtsEnterpriseMenuGroups,
  filterCustomerUserMenuGroups,
  filterQualityManagerMenuGroups,
  filterRcaOwnerOrAdminMenuGroups,
  isRcaOwnerOrPlatformAdminUser,
  isRcaInvestigationUser,
  isCapaOwnerOrPlatformAdminUser,
  isVerificationOwnerOrPlatformAdminUser,
  isQualityManagerUser,
  isCmtsPathActive,
  type CmtsMenuGroup,
} from "@cmts/routes/cmtsEnterpriseNavigation.js"; import type { CmtsHostRuntime } from "../../app/appRuntime.js";
import { clearCmtsSession, readCmtsSession } from "@cmts/auth/authStore.js";
import { resolveCurrentCmtsSession } from "@cmts/session/cmtsSessionResolver.js";
import { formatCmtsTenantDisplay } from "@cmts/session/cmtsTenantDisplay.js";
import { CmtsSessionBadge } from "@components/session/index.js";
import { CmtsCommandCenter } from "@components/command-center/index.js";
import { CmtsNotificationBell, CmtsNotificationDrawer, type CmtsNotificationItem } from "@components/notifications/index.js";
import { FiChevronRight, FiCommand, FiGlobe, FiMenu, FiPlusCircle, FiSearch, FiSidebar, FiX } from "react-icons/fi";
import { throwCmtsCanonicalError } from "@cmts/api/cmtsApiErrors.js";
import { normalizeUserSafeApiError } from "@cmts/api/apiErrorNormalizer.js";
import { authActions } from "@store/authSlice.js";
import { permissionActions } from "@store/permissionSlice.js";
import { tenantActions } from "@store/tenantSlice.js";
type ShellProps = {
  children: ReactNode;
  activePath?: string | undefined;
  diagnostics?: readonly ComponentDiagnostic[] | undefined;
  permissionContext?: PermissionContext | null | undefined;
  runtime?: CmtsHostRuntime | undefined;
};
type CmtsGlobalSearchResult = { id: string; type: string; title: string; subtitle: string; path: string };
type CmtsRuntimeStorageHealth = { mode: "live-backend" | "indexeddb" | "memory-fallback"; lastError?: string | undefined };
const LIVE_BACKEND_STORAGE_HEALTH: CmtsRuntimeStorageHealth = { mode: "live-backend" };
let cmtsGlobalSidebarScrollTop = 0;
function textContains(row: Record<string, unknown>, query: string): boolean {
  return Object.values(row).some((value) => String(value ?? "").toLowerCase().includes(query));
}
function pickText(row: Record<string, unknown>, keys: readonly string[], fallback: string): string {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && String(value).trim()) return String(value);
  }
  return fallback;
}
async function buildCmtsGlobalSearchResults(query: string, _canReadAll: boolean, runtime: CmtsHostRuntime | undefined, tenantId: string | undefined): Promise<readonly CmtsGlobalSearchResult[]> {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  if (!runtime) throwCmtsCanonicalError({ code: "CMTS_ERR_MISSING_EVENT_CONTEXT", message: "Global search requires the live CMTS runtime repository suite.", details: { dependency: "runtime.repositories" } });

  const [complaintsResult, tasksResult, notificationsResult, messagesResult, rcaResult, capaResult, verificationResult] = await Promise.all([
    runtime.repositories.complaints.list({ tenantId, pageNumber: 1, pageSize: 25, searchText: q }),
    runtime.repositories.tasks.my(tenantId),
    runtime.repositories.notifications.my(tenantId),
    runtime.repositories.messages.threads(tenantId),
    runtime.repositories.rca.queue(tenantId, { searchText: q }),
    runtime.repositories.capa.queue(tenantId, { searchText: q }),
    runtime.repositories.verification.queue(tenantId, { searchText: q }),
  ]);

  const records = <T extends Record<string, unknown>>(result: { ok: boolean; data?: unknown } | undefined): readonly T[] => {
    if (!result?.ok || !Array.isArray(result.data)) return [];
    return result.data.filter((item): item is T => typeof item === "object" && item !== null && !Array.isArray(item));
  };

  const complaints = records<Record<string, unknown>>(complaintsResult);
  const tasks = records<Record<string, unknown>>(tasksResult);
  const notifications = records<Record<string, unknown>>(notificationsResult);
  const messages = records<Record<string, unknown>>(messagesResult);
  const rcaRows = records<Record<string, unknown>>(rcaResult);
  const capaRows = records<Record<string, unknown>>(capaResult);
  const verificationRows = records<Record<string, unknown>>(verificationResult);
  const results: CmtsGlobalSearchResult[] = [];
  for (const row of complaints.filter((item) => textContains(item, q)).slice(0, 8)) {
    const id = pickText(row, ["id", "complaintId"], "");
    if (!id) continue;
    results.push({ id: `complaint-${id}`, type: "Complaint", title: `${pickText(row, ["complaintNumber"], id)} — ${pickText(row, ["title"], "Complaint")}`, subtitle: `${pickText(row, ["status", "statusLabel"], "Status")} · ${pickText(row, ["priority"], "Priority")} · ${pickText(row, ["slaStatus"], "SLA")}`, path: `/cmts/complaints/${encodeURIComponent(id)}` });
  }
  for (const row of tasks.filter((item) => textContains(item, q)).slice(0, 5)) {
    const rowId = pickText(row, ["id", "taskId"], "task");
    const complaintId = pickText(row, ["complaintId", "linkedEntityId"], "");
    results.push({ id: `task-${rowId}`, type: "Task", title: pickText(row, ["title", "summary"], "Task"), subtitle: `${pickText(row, ["status"], "Open")} · due ${pickText(row, ["dueAt", "dueDate"], "not set")}`, path: complaintId ? `/cmts/complaints/${encodeURIComponent(complaintId)}/workflow` : "/cmts/tasks" });
  }
  for (const row of notifications.filter((item) => textContains(item, q)).slice(0, 5)) {
    const rowId = pickText(row, ["id", "notificationId"], "notification");
    const complaintId = pickText(row, ["complaintId", "linkedEntityId"], "");
    results.push({ id: `notification-${rowId}`, type: "Notification", title: pickText(row, ["title", "summary"], "Notification"), subtitle: pickText(row, ["priority", "channel"], "In-app"), path: complaintId ? `/cmts/complaints/${encodeURIComponent(complaintId)}` : "/cmts/notifications" });
  }
  for (const row of messages.filter((item) => textContains(item, q)).slice(0, 4)) {
    const rowId = pickText(row, ["id", "messageId"], "message");
    const complaintId = pickText(row, ["complaintId", "linkedEntityId"], "");
    results.push({ id: `message-${rowId}`, type: "Message", title: pickText(row, ["subject", "body", "message"], "Message"), subtitle: pickText(row, ["visibility", "status"], "Thread"), path: complaintId ? `/cmts/complaints/${encodeURIComponent(complaintId)}/messages` : "/cmts/messages" });
  }
  for (const row of rcaRows.filter((item) => textContains(item, q)).slice(0, 4)) {
    const rowId = pickText(row, ["id", "rcaId"], "rca");
    const complaintId = pickText(row, ["complaintId"], "");
    results.push({ id: `rca-${rowId}`, type: "RCA", title: pickText(row, ["problemStatement", "rootCause"], "RCA"), subtitle: pickText(row, ["status", "method"], "RCA"), path: complaintId ? `/cmts/complaints/${encodeURIComponent(complaintId)}/rca` : "/cmts/rca" });
  }
  for (const row of capaRows.filter((item) => textContains(item, q)).slice(0, 4)) {
    const rowId = pickText(row, ["id", "capaId"], "capa");
    const complaintId = pickText(row, ["complaintId"], "");
    results.push({ id: `capa-${rowId}`, type: "CAPA", title: pickText(row, ["description", "actionType"], "CAPA"), subtitle: pickText(row, ["status", "dueDate"], "CAPA"), path: complaintId ? `/cmts/complaints/${encodeURIComponent(complaintId)}/capa` : "/cmts/capa" });
  }
  for (const row of verificationRows.filter((item) => textContains(item, q)).slice(0, 4)) {
    const rowId = pickText(row, ["id", "verificationId"], "verification");
    const complaintId = pickText(row, ["complaintId"], "");
    results.push({ id: `verification-${rowId}`, type: "Verification", title: pickText(row, ["result", "status"], "Verification"), subtitle: pickText(row, ["remarks", "verifierUserId"], "Verification"), path: complaintId ? `/cmts/complaints/${encodeURIComponent(complaintId)}/verification` : "/cmts/verification" });
  }
  return results.slice(0, 12);
}

function notificationId(row: CmtsNotificationItem): string {
  return String(row.id ?? row.notificationId ?? "").trim();
}
const CMTS_NOTIFICATION_TERMINAL_READ_STATUSES = new Set(["read", "acknowledged", "archived", "sent"]);
function isUnread(row: CmtsNotificationItem): boolean {
  return !row.readAt && !CMTS_NOTIFICATION_TERMINAL_READ_STATUSES.has(String(row.status ?? "").toLowerCase());
}
function isCritical(row: CmtsNotificationItem): boolean {
  const priority = String(row.priority ?? "").toLowerCase();
  return priority.includes("critical") || priority.includes("high");
}
export function CmtsAppShell(props: ShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useCmtsDispatch();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [utilityOpen, setUtilityOpen] = useState(true);
  const [commandOpen, setCommandOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState<readonly CmtsNotificationItem[]>([]);
  const [notificationLoadError, setNotificationLoadError] = useState<string | null>(null);
  const [storageHealth, setStorageHealth] = useState<CmtsRuntimeStorageHealth>(LIVE_BACKEND_STORAGE_HEALTH);
  const [globalSearchText, setGlobalSearchText] = useState("");
  const [globalSearchResults, setGlobalSearchResults] = useState<readonly CmtsGlobalSearchResult[]>([]);
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [globalSearchError, setGlobalSearchError] = useState<string | null>(null);
  const globalSearchRef = useRef<HTMLDivElement | null>(null);
  const activePath = props.activePath ?? location.pathname ?? "/cmts";
  const reduxAuth = useCmtsSelector(selectCmtsAuth);
  const reduxPermissions = useCmtsSelector(selectCmtsPermissions);
  const reduxMenuItems = useCmtsSelector(selectCmtsMenuItems);
  const resolvedSession = resolveCurrentCmtsSession();
  const session = resolvedSession.status === "ready" ? resolvedSession.session : null;
  const reduxSession = reduxAuth.session;
  const storedSession = readCmtsSession();
  const permissionSource = reduxPermissions.length > 0 ? reduxPermissions : props.permissionContext?.permissions ?? session?.permissionCodes ?? reduxSession?.permissionCodes ?? storedSession?.permissions ?? [];
  const permissionSignature = Array.isArray(permissionSource) ? permissionSource.join("|") : "";
  const permissions = useMemo(() => Array.from(new Set((permissionSource ?? []).map(String).filter(Boolean))), [permissionSignature]);
  const authenticatedForMenu = reduxAuth.status === "authenticated" || Boolean(session ?? reduxSession ?? storedSession);
  // Backend /auth/my-menu is authoritative after login; route config is used only as a pre-auth/skeleton fallback.
  const isCustomerUser = useMemo(() => {
    const roleValues = [
      ...(session?.roleIds ?? []),
      ...(reduxSession?.roleIds ?? []),
      ...(storedSession?.roleIds ?? []),

      session?.persona ?? "",
      reduxSession?.persona ?? "",
      storedSession?.persona ?? "",
    ].map((value) =>
      String(value)
        .trim()
        .toUpperCase(),
    );

    return roleValues.includes(
      "CUSTOMER_USER",
    );
  }, [
    session,
    reduxSession,
    storedSession,
  ]);

  const isRcaOwnerOrAdmin = useMemo(() => {
    const roleValues = [
      ...(session?.roleIds ?? []),
      ...(reduxSession?.roleIds ?? []),
      ...(storedSession?.roleIds ?? []),
      ...(storedSession?.roles ?? []),
    ];

    const personaVal = (
      session?.persona ||
      reduxSession?.persona ||
      storedSession?.persona ||
      ""
    );

    return isRcaOwnerOrPlatformAdminUser(roleValues, personaVal, permissions);
  }, [session, reduxSession, storedSession, permissions]);

  const isRcaInvestigation = useMemo(() => {
    const roleValues = [
      ...(session?.roleIds ?? []),
      ...(reduxSession?.roleIds ?? []),
      ...(storedSession?.roleIds ?? []),
      ...(storedSession?.roles ?? []),
    ];

    const personaVal = (
      session?.persona ||
      reduxSession?.persona ||
      storedSession?.persona ||
      ""
    );

    return isRcaInvestigationUser(roleValues, personaVal, permissions);
  }, [session, reduxSession, storedSession, permissions]);

  const isCapaOwnerOrAdmin = useMemo(() => {
    const roleValues = [
      ...(session?.roleIds ?? []),
      ...(reduxSession?.roleIds ?? []),
      ...(storedSession?.roleIds ?? []),
      ...(storedSession?.roles ?? []),
    ];

    const personaVal = (
      session?.persona ||
      reduxSession?.persona ||
      storedSession?.persona ||
      ""
    );

    return isCapaOwnerOrPlatformAdminUser(roleValues, personaVal, permissions);
  }, [session, reduxSession, storedSession, permissions]);

  const isVerificationOwnerOrAdmin = useMemo(() => {
    const roleValues = [
      ...(session?.roleIds ?? []),
      ...(reduxSession?.roleIds ?? []),
      ...(storedSession?.roleIds ?? []),
      ...(storedSession?.roles ?? []),
    ];

    const personaVal = (
      session?.persona ||
      reduxSession?.persona ||
      storedSession?.persona ||
      ""
    );

    return isVerificationOwnerOrPlatformAdminUser(roleValues, personaVal, permissions);
  }, [session, reduxSession, storedSession, permissions]);

  const isQualityManager = useMemo(() => {
    const roleValues = [
      ...(session?.roleIds ?? []),
      ...(reduxSession?.roleIds ?? []),
      ...(storedSession?.roleIds ?? []),
      ...(storedSession?.roles ?? []),
    ];

    const personaVal = (
      session?.persona ||
      reduxSession?.persona ||
      storedSession?.persona ||
      ""
    );

    return isQualityManagerUser(roleValues, personaVal, permissions);
  }, [session, reduxSession, storedSession, permissions]);

  const groups = useMemo<
    readonly CmtsMenuGroup[]
  >(() => {
    let resolvedGroups: readonly CmtsMenuGroup[];

    if (reduxMenuItems.length > 0) {
      resolvedGroups = buildCmtsMenuGroupsFromBackendMenu(reduxMenuItems, permissions, isRcaOwnerOrAdmin, isCapaOwnerOrAdmin, isVerificationOwnerOrAdmin, isRcaInvestigation);
    } else if (authenticatedForMenu) {
      resolvedGroups = [];
    } else {
      resolvedGroups = filterCmtsEnterpriseMenuGroups(permissions, isRcaOwnerOrAdmin, isCapaOwnerOrAdmin, isVerificationOwnerOrAdmin, isRcaInvestigation);
    }

    if (isCustomerUser) {
      resolvedGroups = filterCustomerUserMenuGroups(resolvedGroups);
    }

    if (isQualityManager) {
      resolvedGroups = filterQualityManagerMenuGroups(resolvedGroups);
    }

    return filterRcaOwnerOrAdminMenuGroups(resolvedGroups, isRcaOwnerOrAdmin, isCapaOwnerOrAdmin, isVerificationOwnerOrAdmin, isRcaInvestigation);
  }, [
    authenticatedForMenu,
    isCustomerUser,
    isQualityManager,
    isRcaOwnerOrAdmin,
    isRcaInvestigation,
    isCapaOwnerOrAdmin,
    isVerificationOwnerOrAdmin,
    permissions,
    reduxMenuItems,
  ]);
  const reduxMenuCount = reduxMenuItems.length;
  const title = props.runtime?.config.appTitle ?? "SWOT DigiNXT CMTS";
  const subtitle = props.runtime?.config.appSubtitle ?? "Complaint Management and Tracking System";
  const isOffline = props.runtime?.config.resolvedRuntimeMode === "OFFLINE_DEMO";
  const diagnostics = props.diagnostics ?? [];
  const showDiagnostics = (props.runtime?.config.appEnvironment ?? "local") !== "production" && diagnostics.length > 0;
  const breadcrumbs = cmtsBreadcrumbsForPath(activePath);
  void reduxMenuCount;
  const displayName = session?.displayName ?? reduxSession?.displayName ?? "CMTS User";
  const roles = session?.roleIds?.slice(0, 2).join(", ") || reduxSession?.roleIds?.slice(0, 2).join(", ") || "Role scope loading";
  const tenant = session ? formatCmtsTenantDisplay(session) : reduxSession?.tenantName ?? reduxSession?.tenantCode ?? "Session tenant unavailable";
  const unreadCount = notifications.filter(isUnread).length;
  const criticalCount = notifications.filter((row) => isUnread(row) && isCritical(row)).length;
  const utilityPanelRef = useRef<HTMLDivElement | null>(null);

  // --- sidebar scroll position & active link preservation ------------------
  const sidebarNavRef = useRef<HTMLElement | null>(null);
  const sidebarScrollTopRef = useRef(cmtsGlobalSidebarScrollTop);
  const handleSidebarNavScroll = useCallback((event: React.UIEvent<HTMLElement>) => {
    sidebarScrollTopRef.current = event.currentTarget.scrollTop;
    cmtsGlobalSidebarScrollTop = event.currentTarget.scrollTop;
  }, []);
  useLayoutEffect(() => {
    const nav = sidebarNavRef.current;
    if (nav) {
      const allActive = Array.from(nav.querySelectorAll<HTMLAnchorElement>(".cmts-menu-link--active"));
      const exactActiveLink = allActive.find((el) => {
        const href = el.getAttribute("href");
        return href && isCmtsPathActive(activePath, href);
      }) ?? allActive[allActive.length - 1];

      if (exactActiveLink) {
        const navRect = nav.getBoundingClientRect();
        const linkRect = exactActiveLink.getBoundingClientRect();
        if (linkRect.top < navRect.top) {
          nav.scrollTop -= (navRect.top - linkRect.top);
        } else if (linkRect.bottom > navRect.bottom) {
          nav.scrollTop += (linkRect.bottom - navRect.bottom);
        }
        sidebarScrollTopRef.current = nav.scrollTop;
        cmtsGlobalSidebarScrollTop = nav.scrollTop;
      }
    }
  }, [activePath]);
  // -------------------------------------------------------------------------

  useEffect(() => {
    setStorageHealth(LIVE_BACKEND_STORAGE_HEALTH);
  }, [isOffline]);
  const loadNotifications = useCallback(async () => {
    try {
      if (!props.runtime) throwCmtsCanonicalError({ code: "CMTS_ERR_MISSING_EVENT_CONTEXT", message: "Notifications require the live CMTS runtime repository suite.", details: { dependency: "runtime.repositories.notifications" } });
      const result = await props.runtime.repositories.notifications.my(session?.tenantId);
      if (!result.ok) throwCmtsCanonicalError({ code: "CMTS_ERR_ADAPTER_FAILURE", message: result.diagnostics.map((diagnostic) => diagnostic.message).join("; ") || "Notification load failed.", details: { diagnostics: result.diagnostics } });
      setNotifications(result.data as readonly CmtsNotificationItem[]);
      setNotificationLoadError(null);
    } catch (error) {
      setNotifications((previous) => previous);
      setNotificationLoadError(normalizeUserSafeApiError(error).userMessage || "Notification load failed. Existing notification state was preserved.");
    }
  }, [props.runtime, session?.tenantId]);
  useEffect(() => { loadNotifications(); }, [loadNotifications, activePath]);
  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      async function refreshGlobalSearch(): Promise<void> {
        try {
          const results = await buildCmtsGlobalSearchResults(globalSearchText, permissions.includes("COMPLAINT.READ"), props.runtime, session?.tenantId);
          if (!cancelled) {
            setGlobalSearchResults(results);
            setGlobalSearchError(null);
          }
        } catch (error) {
          if (!cancelled) {
            setGlobalSearchResults([]);
            setGlobalSearchError(normalizeUserSafeApiError(error).userMessage || "Global search failed against the backend/API repositories.");
          }
        }
      }
      refreshGlobalSearch();
    }, 160);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [globalSearchText, permissions, props.runtime, session?.tenantId]);
  useEffect(() => {
    if (!globalSearchOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!globalSearchRef.current?.contains(event.target as Node)) setGlobalSearchOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === "Escape") setGlobalSearchOpen(false); };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => { document.removeEventListener("pointerdown", onPointerDown, true); document.removeEventListener("keydown", onKeyDown, true); };
  }, [globalSearchOpen]);
  useEffect(() => {
    setCommandOpen(false);
    setNotificationOpen(false);
    setMobileMenuOpen(false);
    setGlobalSearchOpen(false);
    setUtilityOpen(false);   // <-- add this
  }, [activePath]);
  function executeGlobalSearch() {
    const q = globalSearchText.trim();
    if (q) navigateTo(`/cmts/complaints?q=${encodeURIComponent(q)}`);
  }
  function openGlobalSearchResult(result: CmtsGlobalSearchResult) {
    setGlobalSearchText("");
    setGlobalSearchResults([]);
    navigateTo(result.path);
  }
  function logout() {
    setCommandOpen(false);
    setNotificationOpen(false);
    clearCmtsSession();
    dispatch(permissionActions.permissionsCleared());
    dispatch(tenantActions.tenantCleared());
    dispatch(authActions.logoutSucceeded());
    window.location.replace("/cmts/login");
  }
  function navigateTo(path: string) {
    navigate(path);
    setCommandOpen(false);
    setNotificationOpen(false);
    setMobileMenuOpen(false);
  }
  async function markNotificationRead(id: string) {
    if (!props.runtime) throwCmtsCanonicalError({ code: "CMTS_ERR_SIDE_EFFECT_UNAVAILABLE", message: "Notification repository is not connected to the CMTS app shell.", details: { dependency: "runtime.repositories.notifications" } });
    if (!id.trim()) throwCmtsCanonicalError({ code: "CMTS_ERR_MISSING_ENTITY", message: "Notification cannot be marked read because its persisted id is missing.", details: { entity: "Notification" } });
    const result = await props.runtime.repositories.notifications.markRead(id, session?.tenantId);
    if (!result.ok) throwCmtsCanonicalError({ code: "CMTS_ERR_SIDE_EFFECT_FAILED", message: result.diagnostics?.[0]?.message ?? "Notification read update failed.", details: { action: "notification.markRead", diagnostics: result.diagnostics } });
    await loadNotifications();
  }
  async function markAllRead() {
    const runtime = props.runtime;
    if (!runtime) throwCmtsCanonicalError({ code: "CMTS_ERR_SIDE_EFFECT_UNAVAILABLE", message: "Notification repository is not connected to the CMTS app shell.", details: { dependency: "runtime.repositories.notifications" } });
    const unread = notifications.filter(isUnread);
    const missingIds = unread.filter((row) => !notificationId(row));
    if (missingIds.length > 0) throwCmtsCanonicalError({ code: "CMTS_ERR_MISSING_ENTITY", message: `${missingIds.length} unread notification(s) cannot be marked read because persisted ids are missing.`, details: { missingCount: missingIds.length, entity: "Notification" } });
    await Promise.all(unread.map(async (row) => {
      const result = await runtime.repositories.notifications.markRead(notificationId(row), session?.tenantId);
      if (!result.ok) throwCmtsCanonicalError({ code: "CMTS_ERR_SIDE_EFFECT_FAILED", message: result.diagnostics?.[0]?.message ?? "Notification mark-all-read failed.", details: { action: "notification.markAllRead", diagnostics: result.diagnostics } });
    }));
    await loadNotifications();
  }
  const renderExpandedSidebarLabels = mobileMenuOpen || !sidebarCollapsed;
  const shellClass = [
    "cmts-enterprise-shell",
    sidebarCollapsed ? "cmts-enterprise-shell--collapsed" : "",
    mobileMenuOpen ? "cmts-enterprise-shell--mobile-open" : "",
  ].filter(Boolean).join(" ");
  function handleClickOutside(event: MouseEvent) {
    const target = event.target as Node;
    const isInsidePanel =
      utilityPanelRef.current?.contains(target);
    const isToggleButton = (target as HTMLElement)
      ?.closest(".cmts-topbar-icon");
    if (!isInsidePanel && !isToggleButton) {
      setUtilityOpen(false);
    }
  }
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const toggleUtility = (e: React.MouseEvent) => {
    e.stopPropagation();
    setUtilityOpen((prev) => !prev);
  };
  return (
    <SwotAppShell className={shellClass}>
      {mobileMenuOpen ? <Button intent="button" className="cmts-mobile-backdrop" aria-label="Close mobile menu" aria-hidden="true" tabIndex={-1} onClick={() => setMobileMenuOpen(false)} /> : null}
      <SwotSidebar className="cmts-enterprise-sidebar" labelledBy="CMTS module navigation">
        <div className="cmts-sidebar-brand">
          <div className="cmts-sidebar-logo"><img src="/logo.png" alt="CMTS Logo" className="cmts-logo-img" /></div>
          {renderExpandedSidebarLabels ? <div className="cmts-sidebar-brand-text"><strong>{title}</strong><span>{subtitle}</span></div> : null}
          <Button className="cmts-sidebar-mobile-close" intent="button" aria-label="Close menu" onClick={() => setMobileMenuOpen(false)}><FiX /></Button>
        </div>
        <Button className="cmts-sidebar-toggle" intent="button" onClick={() => setSidebarCollapsed((value) => !value)} aria-pressed={sidebarCollapsed}>
          <FiSidebar aria-hidden="true" /> {sidebarCollapsed ? "Expand" : "Collapse"}
        </Button>
        <nav
          className="cmts-sidebar-nav"
          aria-label="CMTS grouped navigation"
          ref={sidebarNavRef}
          onScroll={handleSidebarNavScroll}
        >
          {groups.map((group) => (
            <section className="cmts-menu-group" key={group.id}>
              {renderExpandedSidebarLabels ? <h2>{group.label}</h2> : null}
              <div className="cmts-menu-items">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = isCmtsPathActive(activePath, item.path);
                  return (
                    <Link key={item.id} className={active ? "cmts-menu-link cmts-menu-link--active" : "cmts-menu-link"} to={item.path} title={item.label} onClick={() => setMobileMenuOpen(false)}>
                      <span className="cmts-menu-link__icon" aria-hidden="true">{Icon ? <Icon size={18} /> : null}</span>
                      {renderExpandedSidebarLabels ? <span className="cmts-menu-link__text">{item.label}</span> : null}
                    </Link>
                  );
                })}
              </div>
            </section>
          ))}
        </nav>
        <div className="cmts-sidebar-footer">
          <a href="https://www.atribsglobal.com/" target="_blank" rel="noopener noreferrer" className="cmts-footer-link" title="ATRIBS GLOBAL">
            <div className="cmts-footer-left"><div className="cmts-footer-icon"><FiGlobe size={16} /></div>{renderExpandedSidebarLabels ? <div className="cmts-footer-text"><p className="cmts-footer-label">Maintained By</p><p className="cmts-footer-company">ATRIBS GLOBAL</p></div> : null}</div>
            {renderExpandedSidebarLabels ? <FiChevronRight size={16} className="cmts-footer-arrow" /> : null}
          </a>
        </div>
      </SwotSidebar>
      <div className="cmts-enterprise-main-frame">
        <SwotHeader className="cmts-enterprise-topbar">
          <Button className="cmts-mobile-menu-button" intent="button" aria-label="Open menu" onClick={() => setMobileMenuOpen(true)}><FiMenu /></Button>
          <Button className="cmts-command-trigger" intent="button" onClick={() => setCommandOpen(true)} aria-haspopup="dialog" aria-expanded={commandOpen} title="Open Command Center"><FiCommand /> Command</Button>
          <div className="cmts-global-search" role="search" ref={globalSearchRef}>
            <FiSearch aria-hidden="true" />
            <Input value={globalSearchText} placeholder="Search complaints, RCA, CAPA, evidence, reports..." aria-label="Global CMTS search" onFocus={() => setGlobalSearchOpen(true)} onChange={(event) => { setGlobalSearchText(event.target.value); setGlobalSearchOpen(true); }} onKeyDown={(event) => { if (event.key === "Enter") executeGlobalSearch(); }} />
            {globalSearchOpen && (globalSearchText.trim().length >= 2 || globalSearchResults.length > 0) ? <div className="cmts-global-search-results" role="listbox" aria-label="Global search results">
              {globalSearchError ? <div className="cmts-global-search-empty" role="alert">{globalSearchError}</div> : globalSearchResults.length > 0 ? globalSearchResults.map((result) => <Button key={result.id} intent="button" role="option" onClick={() => openGlobalSearchResult(result)}>
                <strong>{result.type}</strong><span>{result.title}</span><small>{result.subtitle}</small>
              </Button>) : <div className="cmts-global-search-empty">No matching complaints, tasks, messages or evidence. Press Enter to search the register.</div>}
            </div> : null}
          </div>
          <div className="cmts-topbar-actions">
            <Link className="cmts-topbar-button cmts-topbar-button--primary" to="/cmts/complaints/new"><FiPlusCircle /> Quick Complaint</Link>
            {/* <span className={isOffline ? "cmts-runtime-pill cmts-runtime-pill--offline" : "cmts-runtime-pill"}>{isOffline ? "Offline Demo" : "Live Backend"}</span> */}
            {session ? <CmtsSessionBadge session={session} /> : <span className="cmts-tenant-pill">{tenant}</span>}
            <CmtsNotificationBell unreadCount={unreadCount} criticalCount={criticalCount} onClick={() => setNotificationOpen(true)} />
            <Button
              className="cmts-topbar-icon"
              intent="button"
              onClick={toggleUtility}
              aria-pressed={utilityOpen}
            >
              ⋯
            </Button>
            <div className="cmts-user-chip"><span>{displayName}</span><small>{roles}</small></div>
            <Button className="cmts-logout-button" intent="button" onClick={logout}>Logout</Button>
          </div>
        </SwotHeader>
        {/* {isOffline ? <div className="cmts-offline-mode-banner" role="status">Offline Demo Mode — browser-local training data with enterprise workflow simulation. Live backend remains production source of truth.</div> : null} */}
        {storageHealth.mode === "memory-fallback" ? <div className="cmts-form-error" role="alert">IndexedDB persistence is unavailable. CMTS is running in memory fallback mode and changes will not survive browser reload. {storageHealth.lastError}</div> : null}
        {notificationLoadError ? <div className="cmts-form-error" role="alert">Notifications could not refresh from backend/API storage: {notificationLoadError}</div> : null}
        <div className={utilityOpen ? "cmts-content-with-utility" : "cmts-content-with-utility cmts-content-with-utility--closed"}>
          <SwotMainContent className="cmts-enterprise-content">
            <SwotBreadcrumb className="cmts-page-breadcrumb" items={breadcrumbs} />
            {props.children}
            {showDiagnostics ? <details className="cmts-dev-diagnostics"><summary>Developer diagnostics</summary><RuntimeDiagnosticBanner diagnostics={diagnostics} title="CMTS runtime diagnostics" /></details> : null}
          </SwotMainContent>
          {utilityOpen ? <aside
            ref={utilityPanelRef}
            className="cmts-utility-panel" aria-label="CMTS right utility panel">
            <h2>Operations Spine</h2>
            <div className="cmts-utility-card cmts-utility-card--button" onClick={() => navigateTo("/cmts/tasks")}><strong>Pending actions</strong><span>Open task, workflow, RCA, CAPA and verification queue.</span></div>
            <div className="cmts-utility-card cmts-utility-card--button" onClick={() => navigateTo("/cmts/complaints?slaStatus=breach")}><strong>SLA risk watch</strong><span>Open near-breach and breached complaints.</span></div>
            <div className="cmts-utility-card cmts-utility-card--button" onClick={() => navigateTo("/cmts/admin/calendar")}><strong>Calendar & due events</strong><span>View workflow due dates, SLA events and scheduled jobs.</span></div>
            <div className="cmts-utility-card cmts-utility-card--button" onClick={() => setNotificationOpen(true)}><strong>Notifications</strong><span>{unreadCount} unread notifications. Open linked complaint contexts.</span></div>
            <div className="cmts-utility-card cmts-utility-card--button" onClick={() => navigateTo("/cmts/audit/history")}><strong>Recent audit</strong><span>Workflow and access events based on permission.</span></div>
          </aside> : null}
        </div>
      </div>
      <CmtsCommandCenter open={commandOpen} permissions={permissions} onClose={() => setCommandOpen(false)} onNavigate={navigateTo} onOpenNotifications={() => setNotificationOpen(true)} />
      <CmtsNotificationDrawer open={notificationOpen} notifications={notifications} onClose={() => setNotificationOpen(false)} onNavigate={navigateTo} onMarkRead={markNotificationRead} onMarkAllRead={markAllRead} />
    </SwotAppShell>
  );
}